import type { VoidFunctionComponent } from 'react';

import { FOSSABOT_DEP_UPGRADE_PRS } from '../../../../shared/featureFlags/flagNames';
import { useParams } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import usePermissions from '../../../hooks/api/permissions/usePermissions';
import { useCurrentProject } from '../../../hooks/api/projects/useCurrentProject';
import { Action, ResourceType, useHasPermission } from '../../../shared/permissions';
import { isProjectInCurrentOrg, nuc } from '../../../utils';
import Redirect from '../../generic/Redirect';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import { FossabotPrsConnectionGate } from './FossabotPrsConnectionGate';
import { FossabotPrsSearch } from './FossabotPrsSearch';
import { FossabotPrsTable } from './FossabotPrsTable';
import { FossabotPrStatCards } from './FossabotPrStatCards';
import { useFossabotPrsFilters } from './useFossabotPrsFilters';

import styles from './FossabotPrsPage.module.less';

/**
 * Lists the pull requests fossabot has opened for a project, across every revision.
 * The route sits under the revision container like the other project tabs, so the
 * URL carries a branch/revision even though the list itself is revision-agnostic.
 *
 * The route is registered unconditionally rather than being gated in `routes.tsx`
 * so that this component can redirect anyone who lands on the URL while the
 * feature flag is off. A gated-out route would instead fall through to the
 * catch-all 404, which has no project to redirect back to.
 */
export const FossabotPrsPage: VoidFunctionComponent = () => {
  const params = useParams<Pick<RevisionRouteParams, 'project_id'>>();
  const isFossabotPrsEnabled = useIsFeatureEnabled(FOSSABOT_DEP_UPGRADE_PRS);
  const { isLoading: isLoadingPermissions } = usePermissions();
  const hasPermission = useHasPermission();
  const project = useCurrentProject();
  /*
   * Above the redirects below, since hooks cannot be called conditionally. It
   * only reads the router context, so running it on a page about to redirect
   * costs nothing.
   */
  const { search, setSearch, state, setState, sort, setSort } = useFossabotPrsFilters();

  /*
   * The project index route works out the default branch and forwards on to the
   * summary, so we don't need to wait for the project to load just to redirect.
   */
  const projectSummaryURL = `/projects/${nuc(params.project_id)}`;

  if (!isFossabotPrsEnabled) {
    return <Redirect replace to={projectSummaryURL} />;
  }

  /*
   * Permissions and the project are both fetched, and every check below reads as
   * false until they land. Render nothing rather than bouncing someone who does in
   * fact have access.
   */
  if (isLoadingPermissions || project.status === 'loading') {
    return null;
  }

  /*
   * The subnav hides this tab from anyone without the permission, but the URL is
   * still reachable by hand, and every fossabot read route behind this page is
   * gated on the same permission. Match the two so a viewer gets the same clean
   * redirect as they would with the flag off, rather than a 403-backed page.
   */
  if (!hasPermission({ action: Action.View, resourceType: ResourceType.OrganizationFossabotUpgrade })) {
    return <Redirect replace to={projectSummaryURL} />;
  }

  /*
   * The permission check above only confirms the viewer's own organization allows the
   * fossabot feature, not that they own this particular project. A viewer with that
   * permission could still open a GitHub App project belonging to another organization
   * by hand. `ProjectSubnav` guards the same case with `isProjectInCurrentOrg`; match
   * it here so a direct visit gets the same redirect instead of a page with nothing to
   * show them.
   */
  if (!isProjectInCurrentOrg(project.data?.organizationId ?? 0)) {
    return <Redirect replace to={projectSummaryURL} />;
  }

  /*
   * fossabot only opens PRs against repos imported through the GitHub App, so there
   * is never anything to list on a project imported any other way. The subnav hides
   * the tab on those projects, but the URL stays reachable by hand.
   */
  if (project.data?.vcs_host !== 'github-app') {
    return <Redirect replace to={projectSummaryURL} />;
  }

  return (
    <FossabotPrsConnectionGate>
      <div className={styles.panel}>
        <h1 className={styles.heading}>Pull Requests</h1>
        <p className={styles.subtitle}>
          PRs opened by fossabot for this project, across all revisions.
        </p>
        {/* The cards count every PR in the project, so the filters below them never narrow them. */}
        <FossabotPrStatCards projectLocator={params.project_id} />
        <FossabotPrsSearch search={search} onSearchChange={setSearch} />
        <FossabotPrsTable
          projectLocator={params.project_id}
          search={search}
          state={state}
          onStateChange={setState}
          sort={sort}
          onSortChange={setSort}
        />
      </div>
    </FossabotPrsConnectionGate>
  );
};
